import * as React from 'react';
import { AICoachingMessage } from '../types/coaching';

/**
 * Counts assistant turns that landed while the coaching panel was closed. While
 * the panel is open the newest assistant message stays marked as seen, so the
 * count drops to zero the moment the rep opens it and never climbs behind their
 * back. An unknown seen id — a new lead resets the conversation — counts
 * everything as unread.
 */
export const useUnreadAssistantCount = (messages: AICoachingMessage[], isPanelOpen: boolean): number => {
	const [lastSeenAssistantId, setLastSeenAssistantId] = React.useState<string | null>(null);

	React.useEffect(() => {
		if (!isPanelOpen) {
			return;
		}
		for (let i = messages.length - 1; i >= 0; i--) {
			if (messages[i].role === 'assistant') {
				setLastSeenAssistantId(messages[i].id);
				return;
			}
		}
		setLastSeenAssistantId(null);
	}, [isPanelOpen, messages]);

	return React.useMemo(() => {
		if (isPanelOpen) {
			return 0;
		}
		let unread = 0;
		for (let i = messages.length - 1; i >= 0; i--) {
			if (messages[i].id === lastSeenAssistantId) {
				break;
			}
			if (messages[i].role === 'assistant') {
				unread++;
			}
		}
		return unread;
	}, [isPanelOpen, lastSeenAssistantId, messages]);
};
